import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { AlertService } from 'src/app/alerts/alerts.service';
import { transformError } from 'src/app/common/transformError';
import { DuplicatePaymentEnum } from 'src/app/configuration/enums/duplicate-payment.enum';
import { HttpStatusCodeEnum } from 'src/app/configuration/enums/http-status-code.enum';
import { OriginsEnum } from 'src/app/configuration/enums/origins.enum';
import { PaymentModulesEnum } from 'src/app/configuration/payment-modules/payment-module-class';
import { OrderState } from 'src/app/features/checkout/states/order.state';
import { PaymentConditions, ReadOrderActionParameter } from 'src/app/models/create-order';
import { InstallmentsService } from 'src/app/services/installments.service';
import { MyCartService } from 'src/app/services/my-cart.service';
import { OrderInfoService } from 'src/app/services/order-info.service';
import { AppState } from '../..';
import {
  activeDefaultSpinnerLoadingAction,
  setLoadingDisabled,
  setOrderStatus,
} from '../config/config.actions';
import { selectConfigOrigin } from '../config/config.selectors';
import { setDebtsInfo, setOrderId } from '../payment/payment.actions';
import { selectPaymentModule } from '../payment/payment.selectors';
import {
  checkAndReadOrder,
  createOrderFailure,
  createOrderSucess,
  fetchCreateOrder,
  readOrder,
  readOrderFailure,
  readOrderSucess,
  setPurchaseOrderId,
  setPurchasePaymentConditions,
  verifyPaymentConditions,
  verifyPaymentConditionsSucess,
  verifyStatusOrder,
  verifyStatusOrderSucess,
} from './purchase.actions';

@Injectable()
export class PurchaseEffects {
  paymentModule!: PaymentModulesEnum;
  origin = '';
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private orderInfoService: OrderInfoService,
    private recommendationService: InstallmentsService,
    private myCartService: MyCartService,
    private alertService: AlertService,
    private orderState: OrderState
  ) {
    this.store.select(selectPaymentModule).subscribe((paymentModule) => {
      this.paymentModule = paymentModule;
    });
    this.store.select(selectConfigOrigin).subscribe((origin) => (this.origin = origin));
  }

  fetchCreateOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchCreateOrder),
      switchMap((action) =>
        this.orderInfoService.createOrder(action.order).pipe(
          map((response) => createOrderSucess(response)),
          catchError((error) => {
            const err = transformError(error);
            const isDuplicatePayment =
              err.title?.includes(DuplicatePaymentEnum.PARTIAL_PAYMENT) ||
              err.title?.includes(DuplicatePaymentEnum.ALREADY_PAYMENT);
            if (err.status === HttpStatusCodeEnum.BAD_REQUEST && isDuplicatePayment) {
              this.orderInfoService.handleErrorToDuplicatePayment(err, action.order);
              return of(createOrderFailure());
            }

            if (this.origin === OriginsEnum.LOGGED_AREA) {
              this.alertService.present({
                titleButtonOk: 'Tentar Novamente',
                actionOk: () => {
                  this.store.dispatch(activeDefaultSpinnerLoadingAction());
                  this.store.dispatch(fetchCreateOrder({ order: action.order }));
                },
                icon: 'acordo-cancelado',
                title: 'Não foi possível criar seu pedido.',
                description:
                  'Se o erro persistir entre em contato com o suporte suporte@flexpag.com.',
              });
            }
            return of(createOrderFailure());
          })
        )
      )
    );
  });

  createOrderSucess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createOrderSucess),
      tap((response) => {
        this.orderState.setPaymentConditions(response.payment_conditions);
        this.orderState.actualId = response.order_id;
        this.store.dispatch(setOrderId({ orderId: response!.order_id }));
        this.store.dispatch(setPurchaseOrderId({ id: response!.order_id }));
        this.store.dispatch(
          setPurchasePaymentConditions({
            paymentCondition: response!.payment_conditions,
          })
        );
        this.orderInfoService.setInstallmentNumber(
          Object.keys(response!.payment_conditions.credit.installments).length
        );
        this.orderInfoService.orderCreated.next(true);
      }),
      map((response) => checkAndReadOrder({ orderId: response.order_id }))
    );
  });

  checkAndReadOrder$ = createEffect(() => {
    const actionsMap: Record<string, (action: ReadOrderActionParameter) => Action> = {
      INVOICES_CREATE: (action: ReadOrderActionParameter) => readOrder({ orderId: action.orderId }),
      PAYMENT_METHOD_SELECT: (action: ReadOrderActionParameter) =>
        readOrder({ orderId: action.orderId }),
    };
    return this.actions$.pipe(
      ofType(checkAndReadOrder),
      map((action) => {
        if (this.origin !== OriginsEnum.LNO && this.origin !== OriginsEnum.MINHAS_CONTAS)
          this.recommendationService.handleRecommendationWhenStarting();
        const actionCreator = actionsMap[this.paymentModule];
        return actionCreator ? actionCreator(action) : setLoadingDisabled();
      })
    );
  });

  createOrderFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createOrderFailure),
      map(() => setLoadingDisabled())
    );
  });

  readOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(readOrder),
      switchMap((action) => this.orderInfoService.readOrder(action.orderId)),
      map((response) => readOrderSucess(response)),
      catchError(() => of(readOrderFailure()))
    );
  });

  readOrderSucess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(readOrderSucess),
      tap((response) => {
        const obj = [
          {
            debtSourceName: response.debts[0].principal.debt_source,
            contractAccountNumber:
              response.debts[0].principal.contract_account! ||
              response.debts[0].principal.installation! ||
              response.debts[0].principal.process_number!,
            debtsAmount: response.debts.reduce((totalAmount, debt) => totalAmount + debt.amount, 0),
          },
        ];
        // this.orderService.orderCreated.next(true);
        this.myCartService.setSelectedInvoices(response.debts);
        this.store.dispatch(setDebtsInfo({ debts: obj }));
      }),
      map(() => setLoadingDisabled())
    );
  });

  readOrderFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(readOrderFailure),
      map(() => setLoadingDisabled())
    )
  );

  verifyStatusOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyStatusOrder),
      switchMap(() => this.orderInfoService.checkOrderStatus()),
      map((response) => verifyStatusOrderSucess(response)),
      catchError((error) => {
        // const detail = error.detail
        const statusMatch = error.detail.match(/Status: (\d{3})/);
        if (statusMatch && statusMatch[1] === '404')
          this.store.dispatch(setOrderStatus({ orderStatus: 'not-created' }));
        return of(setLoadingDisabled());
      })
    )
  );

  verifyStatusOrderSucess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyStatusOrderSucess),
      map((action) => {
        if (action.status === 'PENDING')
          this.store.dispatch(setOrderStatus({ orderStatus: 'pending' }));
        // this.store.dispatch(setOrderStatus({ orderStatus:'not-created'}))
        return setLoadingDisabled();
      })
    )
  );

  verifyPaymentConditions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyPaymentConditions),
      mergeMap(({ debtSource, debts, orderId }) =>
        this.orderInfoService.createPaymentConditions(debtSource, debts).pipe(
          map((response) =>
            verifyPaymentConditionsSucess({ responsePaymentConditions: response, orderId: orderId })
          ),
          catchError(() => of(setLoadingDisabled()))
        )
      )
    )
  );

  verifyPaymentConditionsSucess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyPaymentConditionsSucess),
      tap((response) => {
        this.orderState.setPaymentConditions(
          response.responsePaymentConditions as PaymentConditions
        );
        const paymentConditions = response.responsePaymentConditions;
        this.store.dispatch(
          setPurchasePaymentConditions({
            paymentCondition: paymentConditions,
          })
        );
        this.orderInfoService.orderCreated.next(true);
        this.store.dispatch(setLoadingDisabled());
      }),
      map((action) => checkAndReadOrder({ orderId: action.orderId })),
      catchError(() => of(setLoadingDisabled()))
    )
  );
}
